/**
 * ออกบัญชี/รีเซ็ตรหัสผ่านจากแถวในตารางพนักงาน — dialog เดียวต่อพนักงาน
 * ผู้เรียก mount เฉพาะตอนเปิด: ปิดแล้ว state (รวมรหัสชั่วคราว) ถูกทิ้งทั้งหมด
 */

import { loginFailureText, loginStatusOf } from './employeeLoginView';
import { suggestUsername, useEmployeeLogin } from './useEmployeeLogin';
import { StatusBadge } from '../StatusBadge';
import { Modal } from '../components/Modal';
import type { Employee } from '../../lib/rbacAdmin/types';

export interface EmployeeLoginDialogProps {
  readonly employee: Employee;
  readonly canEdit: boolean;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  readonly onEmployeeChanged: () => void;
  readonly onClose: () => void;
}

export function employeeLoginActionLabel(employee: Employee): string {
  return (employee.login_status ?? 'NONE') === 'NONE' ? 'ออกบัญชี/รหัสผ่าน' : 'รีเซ็ตรหัสผ่าน';
}

export function EmployeeLoginDialog({
  employee,
  canEdit,
  apiBaseUrl,
  fetchImpl,
  onEmployeeChanged,
  onClose,
}: EmployeeLoginDialogProps) {
  const login = useEmployeeLogin({
    employee,
    canEdit,
    apiBaseUrl,
    fetchImpl,
    onEmployeeChanged,
    initialUsername: suggestUsername(employee.employee_id),
  });
  const { issued, failure, busy, copyState } = login;
  const isIssue = (employee.login_status ?? 'NONE') === 'NONE';
  const status = loginStatusOf(employee.login_status);

  return (
    <Modal open onClose={onClose} title={`${employeeLoginActionLabel(employee)} — ${employee.display_name}`}>
      <div data-testid="employee-login-dialog">
        <p className="cap-notice__text">
          <code className="cap-table__code">{employee.employee_id}</code>{' '}
          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
        </p>

        {issued !== null ? (
          <div className="cap-notice" data-testid="employee-login-issued" role="region" aria-label="รหัสผ่านชั่วคราว">
            <StatusBadge tone="danger">แสดงครั้งเดียว</StatusBadge>
            <p className="cap-notice__text">
              {issued.username === null ? 'รีเซ็ตรหัสแล้ว' : `ออกบัญชีแล้ว: ${issued.username}`} — ส่งรหัสชั่วคราวนี้ให้พนักงาน
              <strong>เป็นการส่วนตัว</strong> พนักงานต้องเปลี่ยนรหัสผ่านเมื่อเข้าสู่ระบบครั้งแรก
              ระบบเก็บไว้แค่ค่าแฮช ถ้าปิดไปแล้วต้องรีเซ็ตใหม่
            </p>
            <p>
              <code className="cap-table__code" data-testid="employee-login-temporary-password">
                {issued.temporary_password}
              </code>
            </p>
            <div className="cap-notice__actions">
              <button
                type="button"
                className="cap-button cap-button--primary"
                data-testid="employee-login-copy"
                onClick={() => {
                  void login.copy();
                }}
              >
                คัดลอก
              </button>
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="employee-login-dialog-close"
                onClick={onClose}
              >
                ปิด (รหัสจะไม่แสดงอีก)
              </button>
              {copyState === 'copied' ? (
                <span className="cap-notice__note" data-testid="employee-login-copied">
                  คัดลอกแล้ว
                </span>
              ) : null}
              {copyState === 'failed' ? (
                <span className="cap-notice__note" data-testid="employee-login-copy-failed">
                  คัดลอกอัตโนมัติไม่ได้ เลือกข้อความแล้วคัดลอกเอง
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        {failure === null ? null : (
          <p className="cap-notice__text" data-testid="employee-login-failure" role="status">
            <StatusBadge tone="danger">ไม่สำเร็จ</StatusBadge> {loginFailureText(failure)}
          </p>
        )}

        {issued !== null ? null : !canEdit ? (
          <p className="cap-notice__note">ต้องมีสิทธิ์ RBAC_ADMIN + EDIT</p>
        ) : isIssue ? (
          employee.status !== 'ACTIVE' ? (
            <p className="cap-notice__note" data-testid="employee-login-inactive">
              ออกบัญชีได้เมื่อสถานะพนักงานเป็น "ใช้งาน"
            </p>
          ) : (
            <form
              className="cap-login-panel__issue"
              data-testid="employee-login-issue-form"
              aria-label="ออกบัญชีล็อกอินให้พนักงานคนนี้"
              onSubmit={(event) => {
                event.preventDefault();
                void login.issue();
              }}
            >
              <label className="cap-field">
                <span className="cap-field__label">username</span>
                <input
                  className="cap-input"
                  data-testid="employee-login-username"
                  value={login.username}
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={64}
                  onChange={(event) => {
                    login.setUsername(event.target.value.trim().toLowerCase());
                  }}
                />
              </label>
              <button
                type="submit"
                className="cap-button cap-button--primary"
                data-testid="employee-login-issue"
                disabled={busy || !login.usernameValid}
              >
                {busy ? 'กำลังออกบัญชี…' : 'ออกบัญชีล็อกอิน'}
              </button>
              <span className="cap-notice__note cap-login-panel__hint">
                a-z ตัวเล็ก ตัวเลข และ . _ - ยาว 3–64 ตัว เช่น somchai.k
              </span>
            </form>
          )
        ) : (
          <div data-testid="employee-login-reset-confirm">
            <p className="cap-notice__text" data-testid="employee-login-reset-warning">
              รีเซ็ตรหัสของ <strong>{employee.display_name}</strong> ({employee.employee_id})? รหัสเดิมใช้ไม่ได้ทันที{' '}
              <strong>ทุก session ถูกตัด</strong> รวมถึงแอปอื่นที่ล็อกอินผ่าน ERP และบัญชีที่ถูกล็อกจะถูกปลดล็อก
            </p>
            <div className="cap-notice__actions">
              <button
                type="button"
                className="cap-button cap-button--primary"
                data-testid="employee-login-reset-confirm-button"
                disabled={busy}
                onClick={() => {
                  login.setFailure(null);
                  void login.reset();
                }}
              >
                {busy ? 'กำลังดำเนินการ…' : 'ยืนยันรีเซ็ตรหัส'}
              </button>
              <button type="button" className="cap-button cap-button--ghost" disabled={busy} onClick={onClose}>
                ยกเลิก
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
